import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { commentNotificationText, groupedCommentText, likeTitle } from '@tunasong/models'
import type { Alert, NotificationServerEvent } from '@tunasong/models'

/** §3: a toast is a glance; the full comment is one click away */
const toastLimit = 140

/** The server tags a mentioned recipient's comment `mention:{commentId}`, and the others `comments:{threadId}` */
const mentionTagPrefix = 'mention:'

// Define a type for the slice state
export interface NotificationsState {
  /** User Alerts */
  alert: Alert | null

  /** The application is busy and should show a fullpage progress bar */
  isBusy: boolean
}

// Define the initial state using that type
const initialState: NotificationsState = {
  alert: null,
  isBusy: false,
}

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setAlert: (state, action: PayloadAction<Alert | null>) => {
      state.alert = action.payload
    },
    clearAlert: state => {
      state.alert = null
    },
    setNotification: (state, action: PayloadAction<NotificationServerEvent>) => {
      const event = action.payload
      const base = { severity: 'info' as const, duration: 30000, ...event }
      /** A comment carries its thread as `tag` (GH #2795): the toast shows its text, and groups the thread */
      if (!(event.tag && event.title)) {
        state.alert = base
        return
      }
      const previous = state.alert
      /** A mention has its own tag and stands until dismissed: a reply in the thread never replaces it (§6, Joni) */
      if (previous?.tag?.startsWith(mentionTagPrefix) && !event.tag.startsWith(mentionTagPrefix)) {
        return
      }
      /** A like (GH #2796, spec §3.2): the same comment's toast is retitled from the likers it shows, as push does */
      if (event.type === 'like') {
        const likeData = (event.data ?? {}) as { likers?: string[]; isTask?: boolean }
        const shownLikers =
          previous?.tag === event.tag ? ((previous.data as { likers?: string[] } | undefined)?.likers ?? []) : []
        const likers = [...new Set([...shownLikers, ...(likeData.likers ?? [])])]
        const { body } = commentNotificationText({
          title: event.title,
          body: event.body ?? event.message,
          limit: toastLimit,
        })
        state.alert = {
          ...base,
          title: likeTitle(likers, Boolean(likeData.isTask)),
          message: body,
          data: { ...likeData, likers },
        }
        return
      }
      const count = previous?.tag === event.tag ? (previous.count ?? 1) + 1 : 1
      const data = (event.data ?? {}) as { author?: string; place?: string }
      const text = event.body ?? event.message
      const { title, body } =
        count > 1
          ? groupedCommentText({ count, place: data.place, author: data.author, text, limit: toastLimit })
          : commentNotificationText({ title: event.title, body: text, limit: toastLimit })
      state.alert = { ...base, title, message: body, count }
    },
    setBusy: (state, action: PayloadAction<boolean>) => {
      state.isBusy = action.payload
    },
  },
})
